import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  PagamentoPixAutomatico,
  RespostaStatusPixAutomatico,
} from "../../types/PixAutomaticoTypes";
import { useStyles } from "./styles";
import theme from "../../theme";
import UsersService from "../../services/UsersService";
import successIcon from "../../assets/images/checked.png";
import googlePlay from "../../assets/images/google-play.png";
import appleStore from "../../assets/images/app-store.png";

interface ExibicaoQrCodePixProps {
  mensagem: string;
  codigoTransacao: string;
  pagamento: PagamentoPixAutomatico;
  onPagamentoConfirmado?: () => void;
}

const INTERVALO_VERIFICACAO_MS = 5000;

function formatarValor(valor: number): string {
  return valor.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatarDataExpiracao(data: string): string {
  const dataFormatada = new Date(data.replace(" ", "T"));
  if (isNaN(dataFormatada.getTime())) {
    return data;
  }
  return dataFormatada.toLocaleString("pt-BR");
}

function obterLabelStatus(statusPagamento: string, pago: boolean): string {
  if (pago) {
    return "Pagamento confirmado";
  }

  switch (statusPagamento) {
    case "aguardando_pagamento":
      return "Aguardando pagamento";
    case "aguardando_autorizacao":
      return "Aguardando pagamento";
    case "expirado":
      return "Pagamento expirado";
    case "cancelado":
      return "Pagamento cancelado";
    case "pago":
    case "confirmado":
      return "Pagamento confirmado";
    default:
      return statusPagamento || "Verificando status...";
  }
}

function pagamentoFoiConfirmado(
  pagoAtual: boolean,
  statusPagamento: string
): boolean {
  const status = statusPagamento?.toLowerCase() || "";
  return (
    pagoAtual === true ||
    status === "pago" ||
    status === "confirmado" ||
    status === "pago_confirmado"
  );
}

function pagamentoFinalizouSemSucesso(statusPagamento: string): boolean {
  const status = statusPagamento?.toLowerCase() || "";
  return status === "expirado" || status === "cancelado";
}

export function ExibicaoQrCodePix({
  mensagem,
  codigoTransacao,
  pagamento,
  onPagamentoConfirmado,
}: ExibicaoQrCodePixProps) {
  const classes = useStyles();
  const [codigoCopiado, setCodigoCopiado] = useState(false);
  const [verificandoStatus, setVerificandoStatus] = useState(false);
  const [statusAtual, setStatusAtual] = useState(
    pagamento.status || "aguardando_pagamento"
  );
  const [pago, setPago] = useState(pagamento.pago === true);
  const intervaloRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const confirmacaoNotificadaRef = useRef(false);

  const imagemQrCode = pagamento.qr_code_imagem.startsWith("data:")
    ? pagamento.qr_code_imagem
    : `data:image/png;base64,${pagamento.qr_code_imagem}`;

  const pagamentoConfirmado = pagamentoFoiConfirmado(pago, statusAtual);
  const pagamentoEncerrado = pagamentoFinalizouSemSucesso(statusAtual);

  const limparIntervalo = () => {
    if (intervaloRef.current) {
      clearInterval(intervaloRef.current);
      intervaloRef.current = null;
    }
  };

  const consultarStatus = async () => {
    if (!codigoTransacao) {
      return;
    }

    try {
      setVerificandoStatus(true);
      const { data } = await UsersService.consultarStatusPixAutomatico(
        codigoTransacao
      );
      const resposta = data as RespostaStatusPixAutomatico;

      const statusRecebido = resposta?.pagamento?.status || statusAtual;
      const pagoRecebido = resposta?.pagamento?.pago === true;

      if (resposta?.pagamento) {
        setStatusAtual(statusRecebido);
        setPago(pagoRecebido);
      }

      if (
        pagamentoFoiConfirmado(pagoRecebido, statusRecebido) ||
        pagamentoFinalizouSemSucesso(statusRecebido)
      ) {
        limparIntervalo();
      }
    } catch {
      // Mantém o polling sem exibir erro na tela
    } finally {
      setVerificandoStatus(false);
    }
  };

  useEffect(() => {
    if (!codigoTransacao || pagamentoConfirmado) {
      return;
    }

    consultarStatus();
    intervaloRef.current = setInterval(
      consultarStatus,
      INTERVALO_VERIFICACAO_MS
    );

    return () => {
      limparIntervalo();
    };
  }, [codigoTransacao]);

  useEffect(() => {
    if (pagamentoConfirmado && !confirmacaoNotificadaRef.current) {
      confirmacaoNotificadaRef.current = true;
      onPagamentoConfirmado?.();
    }
  }, [pagamentoConfirmado, onPagamentoConfirmado]);

  const copiarCodigoPix = async () => {
    try {
      await navigator.clipboard.writeText(pagamento.qr_code_payload);
      setCodigoCopiado(true);
      setTimeout(() => setCodigoCopiado(false), 3000);
    } catch {
      setCodigoCopiado(false);
    }
  };

  if (pagamentoConfirmado) {
    return (
      <Box className={classes.conteudo}>
        <img
          src={successIcon}
          alt="Icone de sucesso"
        />
        <Typography className={classes.tituloComprovante}>
          Ativação do plano em processamento.
        </Typography>
        <Typography className={classes.subtituloComprovante}>
          Código da transação: {codigoTransacao}
        </Typography>
        <Typography className={classes.subtituloComprovante}>
          Enviamos em seu e-mail de cadastro todas as informações necessárias
          para a ativação do plano.
        </Typography>
        <Typography
          className={classes.subtituloComprovante}
          style={{ color: "#000" }}
        >
          Em breve nossa equipe fará contato com você através do seu telefone de
          contato
        </Typography>
        <Typography className={classes.subtituloComprovante}>
          aproveite e baixa o nosso app nas lojas digitais
        </Typography>
        <Box className={classes.containerImages}>
          <img
            src={googlePlay}
            alt=""
          />
          <img
            src={appleStore}
            alt=""
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.conteudo}>
      <Typography className={classes.titulo}>
        Efetue o pagamento via Pix
      </Typography>
      <Typography className={classes.subtitulo}>{mensagem}</Typography>

      <Box className={classes.containerStatus}>
        {verificandoStatus ? (
          <CircularProgress size={18} sx={{ color: theme.palette.primary.main }} />
        ) : null}
        <Typography
          className={`${classes.textoStatus} ${
            pagamentoEncerrado
              ? classes.statusErro
              : classes.statusAguardando
          }`}
        >
          {obterLabelStatus(statusAtual, pago)}
        </Typography>
      </Box>

      <Box className={classes.containerQrCode}>
        <img
          src={imagemQrCode}
          alt="QR Code Pix"
          className={classes.imagemQrCode}
        />
      </Box>

      <Typography className={classes.infoValor}>
        Valor: {formatarValor(pagamento.valor)}
      </Typography>
      <Typography className={classes.infoDetalhe}>
        Expira em: {formatarDataExpiracao(pagamento.expira_em)}
      </Typography>
      <Typography className={classes.infoDetalhe}>
        Código da transação: {codigoTransacao}
      </Typography>

      {!pagamentoEncerrado && (
        <Button
          variant="contained"
          startIcon={<ContentCopyIcon />}
          onClick={copiarCodigoPix}
          sx={{
            marginTop: "20px",
            backgroundColor: theme.palette.primary.dark,
            color: "#FFF",
            fontSize: "16px",
          }}
        >
          {codigoCopiado ? "Código copiado!" : "Copiar código Pix"}
        </Button>
      )}

      <Typography className={classes.instrucao}>
        {pagamentoEncerrado
          ? "Este QR Code não está mais disponível. Realize um novo cadastro se necessário."
          : "Após o pagamento, esta tela será atualizada automaticamente. Enviamos também as instruções para o seu e-mail de cadastro."}
      </Typography>
    </Box>
  );
}
